import { useState } from 'react'

import {
  EmployeesEmployeeSelectorInput,
  ShiftManagementBulkDeleteShiftsDocument,
} from 'generated/resources/graphql'
import uniq from 'lodash/uniq'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useFetchDefaultShifts from 'modules/shift_management/components/ShiftManagement/MainPage/useFetchDefaultShifts'
import { DaysByRow } from 'modules/shift_management/hooks/reducers/selection'
import { ShiftInputsAttributesByDate } from 'modules/shift_management/hooks/useDefaultAttributesFromRows'
import DefaultShiftWithParamsForm from 'modules/shift_management/lib/forms/DefaultShiftWithParamsForm'
import { trackEditShiftSubmit } from 'modules/shift_management/lib/trackEvents'
import type DefaultShift from 'modules/shift_management/store/internal/models/DefaultShift'
import type Shift from 'modules/shift_management/store/internal/models/Shift'

import { useMutateDefaultShifts } from './useMutateDefaultShifts'
import { useSchedule } from './useSchedule'
import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'

interface ReassignShiftProps {
  employeeSelection: EmployeesEmployeeSelectorInput
  shifts: Shift[]
  defaultAttributes: ShiftInputsAttributesByDate
  daysByRow: DaysByRow
  preselectedSavedShifts: (string | null)[]
  isOriginalIncluded: boolean
  showReassignMessage: boolean
}

export interface ReturnProps {
  forms: DefaultShiftWithParamsForm[]
  onToggleShift: (id: string) => void
  onSubmit: (closePortal?: () => void) => Promise<void>
  submitting: boolean
}

const useReplaceSavedShifts = ({
  employeeSelection,
  shifts,
  defaultAttributes,
  daysByRow,
  preselectedSavedShifts,
  isOriginalIncluded,
  showReassignMessage,
}: ReassignShiftProps): ReturnProps => {
  const [, dispatchShiftsRows] = useShiftsRows()
  const dispatchSelection = useSelectionDispatch()
  const [{ view }] = useSchedule()

  const visibleDefaultShifts = useFetchDefaultShifts({})
  const onlyOnePreselectedSavedShift = preselectedSavedShifts.length === 1

  const [bulkDestroyShifts, { loading }] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
  })

  const preselectSavedShift = (shiftId: string): boolean => {
    if (!onlyOnePreselectedSavedShift) return false
    return preselectedSavedShifts.includes(shiftId)
  }

  const defaultSavedShift = onlyOnePreselectedSavedShift ? shifts[0] : null

  const initializeForms = () => {
    return visibleDefaultShifts.defaultShifts.active.map((defaultShift: DefaultShift) => {
      const { id, title, startAt, endAt, overnightShift, color, locationId, workAreaId, notes } =
        defaultShift

      const isSelected = preselectSavedShift(id) && !!defaultSavedShift

      return new DefaultShiftWithParamsForm({
        id,
        title,
        start_at: startAt,
        end_at: endAt,
        extra_hours: isSelected ? defaultSavedShift.extraHours : false,
        color,
        overnight_shift: overnightShift,
        location_id: isSelected ? defaultSavedShift.locationId : locationId,
        work_area_id: isSelected ? defaultSavedShift.workAreaId : workAreaId,
        notes: isSelected ? defaultSavedShift.notes : notes,
        is_selected: isSelected,
      })
    })
  }

  const [forms] = useState<DefaultShiftWithParamsForm[]>(initializeForms())
  const selectedShiftForms = forms.filter((form) => form.get('is_selected').value)
  const selectedShiftFormsNotes = uniq(selectedShiftForms.map((form) => form.get('notes').value))
  const selectedShiftFormsIds = new Set(selectedShiftForms.map((form) => form.get('id').value))

  const isOnlyEditingShifts =
    preselectedSavedShifts.length === selectedShiftFormsIds.size &&
    preselectedSavedShifts.every((id) => selectedShiftFormsIds.has(id?.toString()))

  const totalNumberOfAssignedEmployees = uniq(Object.keys(daysByRow)).length

  const onlyOneSelectedForm = selectedShiftForms?.length === 1
  const { onSubmit: onSubmitAdd } = useMutateDefaultShifts({
    employeeSelection,
    defaultAttributes,
    daysByRow,
    weekDays: '',
    replaceShifts: true,
  })

  const onToggleShift = (id: string) => {
    const form = forms.find((f) => f.get('id').value === id)

    if (!form) throw 'Could not find form'

    form.get('is_selected').set(!form.get('is_selected').value)
  }

  const onSubmit = async (closePortal?: () => void) => {
    const shiftsIds = shifts.map((shift) => shift.id)
    const result = await bulkDestroyShifts({
      variables: { ids: shiftsIds },
      update: (cache, { data }) => {
        data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts?.forEach((shift) => {
          cache.evict({
            id: cache.identify(shift),
          })
        })
      },
    })
    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      await dispatchShiftsRows({ type: 'DESTROY_SHIFTS', shifts, view })
      onSubmitAdd(selectedShiftForms)

      dispatchSelection({
        type: 'CLEAR_SELECTION',
      })

      await trackEditShiftSubmit({
        shifts: shifts,
        name: onlyOneSelectedForm ? selectedShiftForms[0]?.get('title').value : null,
        startAt: onlyOneSelectedForm ? selectedShiftForms[0]?.get('start_at').value : null,
        endAt: onlyOneSelectedForm ? selectedShiftForms[0]?.get('end_at').value : null,
        notes: onlyOneSelectedForm ? selectedShiftForms[0]?.get('notes').value : null,
        extraHours: onlyOneSelectedForm ? selectedShiftForms[0]?.get('extra_hours').value : null,
        locationId: onlyOneSelectedForm ? selectedShiftForms[0]?.get('location_id').value : null,
        workAreaId: onlyOneSelectedForm ? selectedShiftForms[0]?.get('work_area_id').value : null,
        mixedValueNotes: selectedShiftFormsNotes.length > 1 ? selectedShiftFormsNotes.length : 0,
        view,
        assignedToModified: showReassignMessage,
        originalEmployeeIncluded: isOriginalIncluded,
        totalNumberOfAssignedEmployees: totalNumberOfAssignedEmployees,
        through: 'side_panel',
        replacedShifts: !isOnlyEditingShifts,
      })

      if (closePortal) closePortal()

      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.edit_shifts_success'),
      })
    }
  }

  return {
    forms,
    onToggleShift,
    onSubmit,
    submitting: loading,
  }
}

export default useReplaceSavedShifts
